<div class="page-content-wrapper">
  <app-sidebar-layout [showMenuToggle]="true" [fillHeight]="true">
    <div class="rp-page">
      <div class="table-header">
        <div class="card-main-title">
          <app-tab-main [links]="tabs"></app-tab-main>
        </div>
        <!-- §5.2c — page-level action cluster; Add role lives here, not in the rail (§5.5). -->
        @if (loadState === 'ready' && !isReadonlyContext) {
          <div class="table-actions">
            <div class="create-entity">
              <button type="button" class="add-role-btn" (click)="addRole()">
                <mat-icon>add</mat-icon> {{ 'Add role' | translate }}
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Loading (design-spec §7 state 1): never a blank page. -->
      @if (loadState === 'loading') {
        <div class="rp-loading">
          <app-rings-loader></app-rings-loader>
          <p>{{ 'Loading roles and permission objects…' | translate }}</p>
        </div>
      }

      <!-- Load error (design-spec §7 state 2): a banner + retry, never an empty matrix over a failed load. -->
      @if (loadState === 'error') {
        <div class="rp-error">
          <div class="banner err" role="alert">
            <mat-icon>error_outline</mat-icon>
            <div class="banner-body">
              <p>{{ 'We couldn\'t load roles and permission objects.' | translate }}</p>
              <button type="button" class="btn btn-secondary" (click)="load()">
                <mat-icon>refresh</mat-icon> {{ 'Try again' | translate }}
              </button>
            </div>
          </div>
        </div>
      }

      @if (loadState === 'ready') {
        <div class="roles-layout">
          <app-role-rail
            class="roles-rail"
            [items]="railItems"
            [selectedKey]="selectedKey"
            (selectRole)="onSelectRole($event)"
            (addRole)="addRole()">
          </app-role-rail>
          <div class="detail">
            <!-- Empty — no roles (design-spec §7 state 3). -->
            @if (!railItems.length) {
              <div class="rp-empty">
                <mat-icon>shield</mat-icon>
                <p>{{ 'No roles yet. Every user needs a role. Start from User or Admin and duplicate, or create one from scratch.' | translate }}</p>
                @if (!isReadonlyContext) {
                  <button type="button" class="btn btn-primary" (click)="addRole()">
                    <mat-icon>add</mat-icon> {{ 'Add role' | translate }}
                  </button>
                }
              </div>
            }
            <!-- No selection (rare — the shell auto-selects the first role on load). -->
            @if (railItems.length && !selectedItem) {
              <div class="rp-empty">
                <p>{{ 'Select a role to see its permissions.' | translate }}</p>
              </div>
            }
            @if (selectedItem; as item) {
              <!-- Save result (design-spec §7 states 7–8, §8): clean-save banner or read-back divergence banner. -->
              <app-role-permission-save-result-banner
                class="detail-banner"
                [result]="saveResult"
                (dismiss)="dismissSaveResult()">
              </app-role-permission-save-result-banner>
              <div class="detail-head">
                <div class="detail-title">
                  <h2>
                    @if (item.isSystem) {
                      <span class="detail-name">{{ item.name }}</span>
                    } @else {
                      <input
                        class="detail-name-input"
                        type="text"
                        maxlength="100"
                        [value]="item.role.name || ''"
                        (input)="onNameChange(item, $event)"
                        [placeholder]="'Role name' | translate"
                        [attr.aria-label]="'Role name' | translate" />
                      }
                      @if (item.isSystem) {
                        <span class="status-badge status-badge--system">
                          <mat-icon>lock</mat-icon> {{ 'System' | translate }}
                        </span>
                      }
                      @if (item.isDirty) {
                        <span class="status-badge status-badge--unsaved">{{ 'Unsaved' | translate }}</span>
                      }
                    </h2>
                    @if (item.isSystem) {
                      <p class="detail-desc">{{ item.description }}</p>
                    } @else {
                      <input
                        class="detail-desc-input"
                        type="text"
                        maxlength="255"
                        [value]="item.role.description || ''"
                        (input)="onDescriptionChange(item, $event)"
                        [placeholder]="'Describe what this role is for' | translate"
                        [attr.aria-label]="'Role description' | translate" />
                      }
                      <div class="detail-mfa">
                        <mat-slide-toggle
                          [checked]="item.role.requireMfa"
                          [disabled]="item.isSystem || item.isIntegrationRole || saving"
                          [matTooltip]="item.isIntegrationRole
                            ? ('Integration service roles can\'t require MFA.' | translate)
                            : ''"
                          (change)="onRequireMfaChange(item, $event.checked)">
                          {{ 'Require MFA' | translate }}
                        </mat-slide-toggle>
                        <p class="detail-mfa-help">
                          {{ 'Users signing in with Google or Microsoft SSO satisfy this through their identity provider and won\'t be asked for a LEAP code.' | translate }}
                        </p>
                      </div>
                    </div>
                    <div class="detail-side">
                      <div class="detail-meta">
                        <div class="dm">
                          <div class="dm-label">{{ 'Objects granted' | translate }}</div>
                          <div class="dm-value">{{ item.grantedCount }} / {{ item.totalCount }}</div>
                        </div>
                      </div>
                      <div class="detail-actions">
                        @if (canStartViewAs && item.role.id) {
                          <button
                            type="button"
                            class="btn btn-secondary detail-view-as"
                            [disabled]="saving"
                            (click)="viewAsRole(item)">
                            <mat-icon>visibility</mat-icon> {{ 'View as…' | translate }}
                          </button>
                        }
                        @if (!isReadonlyContext) {
                          <button
                            type="button"
                            class="btn btn-secondary detail-clone"
                            [disabled]="saving"
                            (click)="cloneRole(item)">
                            <mat-icon>content_copy</mat-icon> {{ 'Clone role' | translate }}
                          </button>
                          @if (!item.isSystem) {
                            <button
                              type="button"
                              class="btn btn-danger detail-delete"
                              [disabled]="saving"
                              (click)="deleteRole(item)">
                              <mat-icon>delete_outline</mat-icon> {{ 'Delete role' | translate }}
                            </button>
                          }
                        }
                      </div>
                    </div>
                  </div>
                  <div class="detail-body">
                    <app-role-permission-matrix
                      [objects]="objects"
                      [rolePermissions]="item.role.rolePermissions"
                      [dirtyObjectIds]="saveDiff.dirtyObjectIds"
                      [divergentObjectIds]="divergentObjectIds"
                      (permissionChange)="onPermissionChange(item, $event)"
                      (bulkVerbChange)="onBulkVerbChange(item, $event)">
                    </app-role-permission-matrix>
                  </div>
                  <!-- Sticky save bar (design-spec §5.6, §8). Review switches the matrix to the Changed filter. -->
                  @if (!isReadonlyContext) {
                  <app-role-permission-save-bar
                    [diff]="saveDiff"
                    [saving]="saving"
                    [canSave]="canSave"
                    [metadataDirty]="metadataDirty"
                    [isNew]="isNewRole"
                    [objectCount]="item.totalCount"
                    (save)="save(item)"
                    (discard)="discard(item)"
                    (review)="reviewChanges()">
                  </app-role-permission-save-bar>
                  }
                }
              </div>
            </div>
          }
        </div>
      </app-sidebar-layout>
    </div>
